import { useEffect, useState, useRef } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, FileText, Receipt, Paperclip, UploadSimple, Trash, PencilSimple, DownloadSimple, X } from "@phosphor-icons/react";

const DOC_TYPES = [
  ["tax_invoice", "Tax invoice"], ["proforma_invoice", "Proforma"], ["delivery_challan", "Delivery challan"],
  ["jobwork_challan", "Jobwork challan"], ["credit_note", "Credit note"], ["debit_note", "Debit note"],
  ["payment_receipt", "Payment receipt"], ["purchase_invoice", "Purchase invoice"], ["exchange_document", "Exchange doc"],
];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ doc_type: "tax_invoice", customer: { name: "" }, items: [{ sku: "", description: "", qty: 1, unit_price: 0, discount: 0, cgst: 0, sgst: 0, igst: 0 }], shipping_charges: 0 });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set("doc_type", filter);
      const { data } = await api.get(`/documents?${params}`);
      setDocs(data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    const items = form.items.map((it) => ({
      ...it,
      qty: Number(it.qty), unit_price: Number(it.unit_price), discount: Number(it.discount),
      cgst: Number(it.cgst), sgst: Number(it.sgst), igst: Number(it.igst),
      taxable: Number(it.qty) * Number(it.unit_price) - Number(it.discount),
      total: Number(it.qty) * Number(it.unit_price) - Number(it.discount) + Number(it.cgst) + Number(it.sgst) + Number(it.igst),
    }));
    try {
      await api.post("/documents", { ...form, items, shipping_charges: Number(form.shipping_charges) });
      toast.success("Document created"); setShowNew(false); load();
    } catch (e) { toast.error("Create failed"); }
  };

  const generateEway = async (invoice_id) => {
    try {
      await api.post("/documents/eway", { invoice_id, transporter_name: "Blue Dart", distance_km: 20, vehicle_number: "MH02XX0000" });
      toast.success("E-Way Bill generated (MOCKED)");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="documents-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Invoicing, challans & e-way bills" description="GST-compliant document generation with sequential numbering and full audit trail." tint="gold" icon={Receipt} actions={
        <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-doc-btn"><Plus size={14}/> New document</button>
      }/>
      <div className="flex gap-3 mb-6">
        <select value={filter} onChange={(e)=>setFilter(e.target.value)}
                className="input-soft max-w-[240px]" data-testid="doc-type-filter">
          <option value="">All types</option>
          {DOC_TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr><th>Number</th><th>Type</th><th>Customer</th><th>Date</th><th>Total</th><th>Payment</th><th></th></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d._id} data-testid={`doc-row-${d.doc_number}`}>
                <td className="font-mono text-xs">{d.doc_number}</td>
                <td className="text-sm text-muted-foreground capitalize">{d.doc_type.replace(/_/g," ")}</td>
                <td className="font-medium">{d.customer?.name || "—"}</td>
                <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(d.date)}</td>
                <td className="tabular font-medium">{inr(d.total)}</td>
                <td><StatusChip value={d.payment_status}/></td>
                <td className="text-right">
                  <div className="flex gap-1.5 justify-end">
                    <button onClick={()=>setSelected(d)} className="btn-ghost btn-sm" data-testid={`doc-view-${d.doc_number}`}><FileText size={12}/> View</button>
                    {d.doc_type === "tax_invoice" && (
                      <button onClick={()=>generateEway(d._id)} className="btn-ghost btn-sm" data-testid={`doc-eway-${d.doc_number}`}>E-way</button>
                    )}
                    <RecordActions entity="documents" record={d} frozen={["doc_number"]} onChanged={load} />
                  </div>
                </td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={7}><EmptyState icon={Receipt} title="No documents yet" description="Generate an invoice, challan or credit note to begin." /></td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 modal-backdrop flex fade-in" onClick={()=>setSelected(null)}>
          <div className="ml-auto w-full max-w-3xl h-full bg-card border-l border-border overflow-y-auto drawer-slide" onClick={(e)=>e.stopPropagation()} data-testid="doc-drawer">
            <div className="p-7 border-b border-border sticky top-0 bg-card z-10">
              <div className="label-eyebrow capitalize">{selected.doc_type.replace(/_/g," ")}</div>
              <div className="display-2 mt-2">{selected.doc_number}</div>
              <div className="text-sm text-muted-foreground mt-2">{fmtDate(selected.date)} · GSTIN {selected.business?.gstin}</div>
            </div>
            <div className="p-7">
              <div className="grid grid-cols-2 gap-8 text-sm">
                <div>
                  <div className="label-eyebrow mb-2">From</div>
                  <div className="font-display text-lg font-semibold">{selected.business?.name}</div>
                  <div className="text-muted-foreground leading-relaxed mt-1">{selected.business?.address}</div>
                  <div className="text-muted-foreground mt-1">GSTIN {selected.business?.gstin}</div>
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Bill to</div>
                  <div className="font-display text-lg font-semibold">{selected.customer?.name}</div>
                  <div className="text-muted-foreground leading-relaxed mt-1">{selected.billing_address?.line1}</div>
                  <div className="text-muted-foreground">{selected.billing_address?.city}, {selected.billing_address?.state} {selected.billing_address?.pin}</div>
                </div>
              </div>
              <div className="card-soft p-0 overflow-x-auto mt-8">
                <table className="table-modern text-sm">
                  <thead><tr><th>SKU</th><th>Description</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Taxable</th><th>Tax</th><th>Total</th></tr></thead>
                  <tbody>
                    {selected.items?.map((i, idx) => (
                      <tr key={idx}>
                        <td className="font-mono text-xs">{i.sku}</td>
                        <td>{i.description}</td>
                        <td className="text-xs">{i.hsn || i.hsn_code || "—"}</td>
                        <td className="tabular">{i.qty}</td>
                        <td className="tabular">{inr(i.unit_price)}</td>
                        <td className="tabular">{inr(i.taxable)}</td>
                        <td className="tabular text-muted-foreground">{inr((i.cgst || 0) + (i.sgst || 0) + (i.igst || 0))}</td>
                        <td className="tabular font-medium">{inr(i.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-8">
                <div className="text-xs text-muted-foreground">
                  <div className="label-eyebrow mb-2">Exchange policy</div>
                  <p className="leading-relaxed">{selected.exchange_policy_note || "Exchange available within 7 days. Refunds only in exceptional cases."}</p>
                </div>
                <div className="text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular">{inr(selected.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="tabular">{inr(selected.cgst)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="tabular">{inr(selected.sgst)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="tabular">{inr(selected.igst)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="tabular">{inr(selected.shipping_charges)}</span></div>
                  <div className="flex justify-between font-display text-xl font-semibold pt-3 border-t border-border/70 mt-2"><span>Total</span><span className="tabular">{inr(selected.total)}</span></div>
                </div>
              </div>
              <AttachmentsSection invoiceId={selected._id} />
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-doc-form">
            <div className="label-eyebrow">New document</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <div className="grid grid-cols-2 gap-5">
              <label className="block">
                <span className="label-eyebrow">Type</span>
                <select value={form.doc_type} onChange={(e)=>setForm({...form,doc_type:e.target.value})}
                        className="input-soft mt-2" data-testid="doc-input-type">
                  {DOC_TYPES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-eyebrow">Customer name</span>
                <input value={form.customer.name} onChange={(e)=>setForm({...form, customer:{...form.customer, name:e.target.value}})}
                       className="input-underline" data-testid="doc-input-customer" required/>
              </label>
            </div>
            <div className="label-eyebrow mt-8 mb-3">Line items</div>
            {form.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-6 gap-2 mb-2">
                <input placeholder="SKU" value={it.sku} onChange={(e)=>{const items=[...form.items]; items[idx].sku=e.target.value; setForm({...form,items});}} className="input-cell col-span-2" data-testid={`doc-item-sku-${idx}`}/>
                <input placeholder="Description" value={it.description} onChange={(e)=>{const items=[...form.items]; items[idx].description=e.target.value; setForm({...form,items});}} className="input-cell col-span-2"/>
                <input placeholder="Qty" type="number" value={it.qty} onChange={(e)=>{const items=[...form.items]; items[idx].qty=e.target.value; setForm({...form,items});}} className="input-cell"/>
                <input placeholder="Price" type="number" value={it.unit_price} onChange={(e)=>{const items=[...form.items]; items[idx].unit_price=e.target.value; setForm({...form,items});}} className="input-cell"/>
              </div>
            ))}
            <button type="button" onClick={()=>setForm({...form,items:[...form.items,{sku:"",description:"",qty:1,unit_price:0,discount:0,cgst:0,sgst:0,igst:0}]})} className="btn-ghost btn-sm mt-2">+ Add line</button>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="doc-submit">Create document</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function AttachmentsSection({ invoiceId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/documents/${invoiceId}/attachments`); setRows(data); }
    catch { toast.error("Could not load attachments"); }
  };
  useEffect(() => { if (invoiceId) load(); /* eslint-disable-next-line */ }, [invoiceId]);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("access_token");
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("description", "");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${invoiceId}/attachments`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { const t = await res.text(); throw new Error(t.slice(0, 120)); }
      }
      toast.success(`Uploaded ${list.length} file${list.length > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(`Upload failed: ${e.message}`); }
    setBusy(false);
  };

  const download = (a) => {
    const token = localStorage.getItem("access_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/attachments/${a._id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob); const dl = document.createElement("a");
        dl.href = url; dl.download = a.filename; document.body.appendChild(dl); dl.click(); dl.remove();
      }).catch(() => toast.error("Download failed"));
  };

  const preview = (a) => {
    const token = localStorage.getItem("access_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/attachments/${a._id}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const url = URL.createObjectURL(blob); window.open(url, "_blank"); })
      .catch(() => toast.error("Preview failed"));
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"? This cannot be undone.`)) return;
    try { await api.delete(`/documents/attachments/${a._id}`); toast.success("Attachment deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const saveRename = async () => {
    try {
      await api.patch(`/documents/attachments/${editRow._id}`, { filename: editRow.filename, description: editRow.description });
      toast.success("Attachment updated"); setEditRow(null); load();
    } catch { toast.error("Update failed"); }
  };

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(2)} MB`;

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <div><div className="label-eyebrow flex items-center gap-1.5"><Paperclip size={12}/> Attachments</div><div className="font-display text-lg font-semibold mt-1">Supporting documents</div></div>
        <span className="text-xs text-muted-foreground tabular">{rows.length} file{rows.length !== 1 ? "s" : ""}</span>
      </div>
      <div
        onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
        onDragLeave={()=>setDrag(false)}
        onDrop={(e)=>{e.preventDefault(); setDrag(false); upload(e.dataTransfer.files);}}
        onClick={()=>inputRef.current?.click()}
        className={`upload-zone ${drag ? "is-drag" : ""}`}
        data-testid="invoice-attach-dropzone"
      >
        <UploadSimple size={22} className="text-[color:hsl(var(--tint-indigo))] mx-auto mb-2" weight="duotone"/>
        <div className="font-display font-semibold text-sm">Drag & drop or click to upload</div>
        <div className="text-xs text-muted-foreground mt-1">PDF · Word · Excel · Images · CSV · TXT · up to 15 MB each</div>
        <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.gif,.csv,.txt" className="hidden" onChange={(e)=>upload(e.target.files)} data-testid="invoice-attach-input"/>
      </div>
      {busy && <div className="text-xs text-muted-foreground text-center mt-3">Uploading…</div>}
      {rows.length > 0 && (
        <div className="card-soft p-0 mt-4 overflow-x-auto">
          <table className="table-modern text-sm">
            <thead><tr><th>File</th><th>Description</th><th>Size</th><th>Uploaded by</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a._id} data-testid={`invoice-attach-row-${a._id}`}>
                  <td><button onClick={()=>preview(a)} className="font-medium underline underline-offset-2 hover:no-underline text-left" data-testid={`invoice-attach-preview-${a._id}`}>{a.filename}</button><div className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">{a.mime_type.split("/").pop()}</div></td>
                  <td className="text-xs text-muted-foreground max-w-xs truncate">{a.description || "—"}</td>
                  <td className="tabular text-xs">{fmtSize(a.size_bytes)}</td>
                  <td className="text-xs text-muted-foreground">{a.uploaded_by}</td>
                  <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={()=>download(a)} className="btn-ghost btn-icon" title="Download" data-testid={`invoice-attach-download-${a._id}`}><DownloadSimple size={12}/></button>
                      <button onClick={()=>setEditRow({...a})} className="btn-ghost btn-icon" title="Rename / describe" data-testid={`invoice-attach-edit-${a._id}`}><PencilSimple size={12}/></button>
                      <button onClick={()=>del(a)} className="btn-ghost btn-icon text-destructive" title="Delete" data-testid={`invoice-attach-delete-${a._id}`}><Trash size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editRow && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setEditRow(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale w-full max-w-md p-7" data-testid="invoice-attach-edit-form">
            <div className="flex items-center justify-between mb-5">
              <div><div className="label-eyebrow">Edit attachment</div><div className="display-3 mt-1">Rename or describe</div></div>
              <button onClick={()=>setEditRow(null)} className="btn-ghost btn-icon"><X size={14}/></button>
            </div>
            <label className="block mb-4"><span className="label-eyebrow">Filename</span>
              <input value={editRow.filename} onChange={(e)=>setEditRow({...editRow, filename:e.target.value})} className="input-underline" data-testid="invoice-attach-rename"/></label>
            <label className="block mb-4"><span className="label-eyebrow">Description</span>
              <textarea value={editRow.description || ""} onChange={(e)=>setEditRow({...editRow, description:e.target.value})} className="textarea-soft mt-2" data-testid="invoice-attach-describe"/></label>
            <div className="flex gap-2 mt-2">
              <button onClick={()=>setEditRow(null)} className="btn-ghost flex-1">Cancel</button>
              <button onClick={saveRename} className="btn-primary flex-1" data-testid="invoice-attach-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
